import {
  Blanket,
  Box,
  Divider,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  Pagination,
  Spinner,
  Text,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { PointerSensor, PointerActivationConstraints } from '@dnd-kit/dom';
import plur from 'plur';
import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sentryCaptureException } from '@/utils/sentry';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAnalytics } from '@/services/analytics';
import { type Asset } from '@/services/db/asset/entity';
import { useMoveAssets } from '@/services/db/asset/mutations';
import { listAssetsQueryOptions } from '@/services/db/asset/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';

import { getFileExtension, preventActivationOnInteractiveChildren } from '../utils';
import { AssetGrid } from './components/asset-grid';
import { AssetList } from './components/asset-list';
import { FolderBreadcrumbs } from './components/folder-breadcrumbs';
import * as styles from './components/index.css';
import { SelectionActions } from './components/selection-actions';
import { useAssetFilters } from './hooks/use-asset-filters';
import { useAssetPermissions } from './hooks/use-asset-permissions';
import { useFolderViewedTracking } from './hooks/use-folder-viewed-tracking';
import { type FilterProps, type NavigationProps, type SelectionProps, type ViewProps } from './types';
import { canDeleteAsset } from './utils/asset-validation';

const ELEMENTS_PER_PAGE = 50;

export const Assets = memo(function Assets({
  enabled,
  navigation,
  selection,
  filters,
  view,
  onSelect,
}: {
  enabled: boolean;
  navigation: NavigationProps;
  selection: SelectionProps;
  filters: FilterProps;
  view: ViewProps;
  onSelect?: (asset: Asset) => void;
}) {
  const { track } = useAnalytics();
  const workspaceId = requireWorkspaceId();
  const [activeId, setActiveId] = useState<string | null>(null);
  const snackbars = useSnackbars();
  const permissions = useAssetPermissions();
  const autoCheckedIdRef = useRef<string | null>(null);
  const qc = useQueryClient();

  const moveAssetsMutation = useMoveAssets();

  const previousSearchRef = useRef('');
  useEffect(() => {
    const isSearchActive = filters.search.query.length > 2;
    const wasSearchActive = previousSearchRef.current.length > 2;

    if (isSearchActive && !wasSearchActive) {
      view.setPageNumber(1);
      selection.onClearCheckedAssets();
      track('assetLibrary', 'searchStarted', {
        search_query: filters.search.query,
      });
    }
    previousSearchRef.current = filters.search.query;
  }, [filters.search.query, view, selection, track]);

  const searchIsActive = filters.search.query.length > 2;

  const { data, isLoading, error } = useQuery({
    ...listAssetsQueryOptions(
      workspaceId,
      {
        folder_id: navigation.parentFolderId ?? '',
        order: view.sortAsc ? 'asc' : 'desc',
        page: view.pageNumber.toString(),
        // The Go API expects base64-encoded UTF-8 search queries.
        // btoa(unescape(encodeURIComponent(str))) is the browser equivalent
        // of Node's Buffer.from(str, 'utf8').toString('base64').
        search: searchIsActive ? btoa(unescape(encodeURIComponent(filters.search.query))) : '',
        sort: view.sortBy,
        limit: ELEMENTS_PER_PAGE,
      },
      qc,
    ),
    enabled,
  });

  const onParentsChangeRef = useRef(navigation.onParentsChange);
  onParentsChangeRef.current = navigation.onParentsChange;

  useEffect(() => {
    if (data?.parents != null) {
      const reversedParents = [...data.parents].reverse();
      onParentsChangeRef.current(reversedParents);
    }
  }, [data?.parents]);

  const { filteredAssets, unselectableReasons: unselectableAssetReasons } = useAssetFilters([...(data?.assets ?? [])], {
    parentFolderId: navigation.parentFolderId,
    isSearchMode: searchIsActive,
    filterPdfs: filters.filterPdfs,
    filterFileSizeInBytes: filters.filterFileSizeInBytes,
    isFilteringByBytesEnabled: filters.isFilteringByBytesEnabled,
  });

  const sortedAssets = useMemo(() => {
    const folders = filteredAssets.filter((asset) => asset.is_dir);
    const files = filteredAssets.filter((asset) => !asset.is_dir);
    return [...folders, ...files];
  }, [filteredAssets]);

  useFolderViewedTracking({
    parentFolderId: navigation.parentFolderId,
    isLoading,
    error: error != null,
    searchIsActive,
    filteredAssets,
    folderPaths: navigation.folderPaths,
  });

  const currentPage = data?.meta.pagination.page ?? 1;

  const handlePageChange = useCallback(
    (newPage: number) => {
      view.setPageNumber(newPage);
      selection.onClearCheckedAssets();
      const meta = data?.meta;
      if (meta != null) {
        const totalPages = Math.ceil(meta.pagination.total / meta.pagination.size);
        track('assetLibrary', 'pagination', {
          page: newPage,
          total_pages: totalPages,
        });
      }
    },
    [data, view, selection, track],
  );

  const onFolderClick = useCallback(
    (asset: Asset) => {
      if (selection.checkedAssetIds.size > 0) return;
      track('assetLibrary', 'folderNavigation', {
        folder_name: asset.name,
        folder_id: asset.id,
      });
      navigation.navigateToFolder(asset.id, asset);
      view.setPageNumber(1);
      filters.setSearchInput('');
      filters.setSearchQuery('');
      selection.onClearCheckedAssets();
    },
    [selection, navigation, view, filters, track],
  );

  const onAssetClick = useCallback(
    (asset: Asset) => {
      if (onSelect == null) return;
      if (selection.checkedAssetIds.size > 0) return;
      if (unselectableAssetReasons[asset.id] != null) return;

      const currentFolder = navigation.folderPaths.at(-1);
      track('assetLibrary', 'fileSelected', {
        asset_id: asset.id,
        file_type: getFileExtension(asset.name),
        folder_name: currentFolder?.name ?? '',
        folder_id: navigation.parentFolderId,
      });
      onSelect(asset);
    },
    [
      navigation.folderPaths,
      navigation.parentFolderId,
      onSelect,
      selection.checkedAssetIds,
      unselectableAssetReasons,
      track,
    ],
  );

  const onDeleteCheckedAssetsClick = useCallback(() => {
    const assets = filteredAssets.filter((asset) => selection.checkedAssetIds.has(asset.id));
    if (assets.length === 0) return;
    selection.onDeleteCheckedAssets(assets);
  }, [filteredAssets, selection.checkedAssetIds, selection]);

  const handleDragStart = useCallback(
    (event: { operation: { source: { id: string | number } | null } }) => {
      const assetId = String(event.operation.source?.id);
      setActiveId(assetId);

      if (selection.checkedAssetIds.has(assetId)) {
        autoCheckedIdRef.current = null;
      } else {
        selection.onCheckAsset(true, assetId);
        autoCheckedIdRef.current = assetId;
      }
    },
    [selection.checkedAssetIds, selection],
  );

  const handleDragEnd = useCallback(
    async (event: {
      operation: {
        source: { id: string | number; data?: Record<string, unknown> } | null;
        target: { id: string | number; data?: Record<string, unknown> } | null;
      };
      canceled?: boolean;
    }) => {
      const { source, target } = event.operation;
      setActiveId(null);

      const autoCheckedId = autoCheckedIdRef.current;
      autoCheckedIdRef.current = null;

      const undoAutoCheck = () => {
        if (autoCheckedId != null) {
          selection.onCheckAsset(false, autoCheckedId);
        }
      };

      // If drag was canceled, undo the auto-check from handleDragStart
      if (event.canceled === true || target == null || source == null || source.id === target.id) {
        undoAutoCheck();
        return;
      }

      const effectiveCheckedIds = new Set(selection.checkedAssetIds);
      if (autoCheckedId != null) {
        effectiveCheckedIds.add(autoCheckedId);
      }

      let targetFolderId: string | null = null;
      let targetFolderName: string | null = null;

      if (typeof target.id === 'string' && target.id.startsWith('breadcrumb-')) {
        targetFolderId = (target.data?.folderId as string) ?? null;
        const breadcrumbFolder = navigation.folderPaths.find((f) => f.id === targetFolderId);
        targetFolderName = breadcrumbFolder?.name ?? null;
      } else {
        const targetAsset = filteredAssets.find((a) => a.id === String(target.id));
        if (targetAsset == null || !targetAsset.is_dir) {
          undoAutoCheck();
          return;
        }
        targetFolderId = targetAsset.id;
        targetFolderName = targetAsset.name;
      }

      if (targetFolderName === null) {
        undoAutoCheck();
        return;
      }

      const sourceId = String(source.id);
      const isDraggedItemChecked = effectiveCheckedIds.has(sourceId);
      const assetsToMove = isDraggedItemChecked
        ? filteredAssets.filter((asset) => effectiveCheckedIds.has(asset.id) && asset.id !== targetFolderId)
        : filteredAssets.filter((asset) => asset.id === sourceId && asset.id !== targetFolderId);

      if (assetsToMove.length === 0) {
        undoAutoCheck();
        return;
      }

      try {
        await moveAssetsMutation.mutateAsync({
          assets: assetsToMove.map((asset) => ({
            asset: {
              id: asset.id,
              name: asset.name,
              is_dir: asset.is_dir,
              parent_id: targetFolderId,
              path: asset.path,
              size: asset.size,
            },
          })),
        });

        snackbars.create({
          variant: 'success',
          message: `Moved ${assetsToMove.length} ${plur('item', assetsToMove.length)} to ${targetFolderName}`,
        });

        selection.onClearCheckedAssets();
      } catch (error) {
        sentryCaptureException(error);
        snackbars.create({
          variant: 'error',
          message: 'Failed to move assets',
        });
        undoAutoCheck();
      }
    },
    [filteredAssets, selection.checkedAssetIds, moveAssetsMutation, snackbars, selection, navigation.folderPaths],
  );

  const sensors = useMemo(
    () => [
      PointerSensor.configure({
        activationConstraints: [new PointerActivationConstraints.Distance({ value: 8 })],
        preventActivation: preventActivationOnInteractiveChildren,
      }),
    ],
    [],
  );

  const getSensors = useCallback(() => sensors, [sensors]);

  const activeAsset = useMemo(
    () => (activeId != null ? filteredAssets.find((a) => a.id === activeId) ?? null : null),
    [activeId, filteredAssets],
  );

  const selectedCount = useMemo(
    () => filteredAssets.filter((asset) => selection.checkedAssetIds.has(asset.id)).length,
    [filteredAssets, selection.checkedAssetIds],
  );

  useEffect(() => {
    document.body.classList.toggle(styles.bodyDraggingAsset, activeAsset != null);
    return () => {
      document.body.classList.remove(styles.bodyDraggingAsset);
    };
  }, [activeAsset]);

  if (isLoading) {
    return (
      <Blanket withBackground={false} isActive={isLoading}>
        <Spinner />
      </Blanket>
    );
  }

  if (error != null) {
    return (
      <Box py="500">
        <EmptyState>
          <EmptyStateTitle icon="error">Error loading assets</EmptyStateTitle>
        </EmptyState>
      </Box>
    );
  }

  if (filteredAssets.length === 0) {
    return (
      <>
        <Box display="flex" justifyContent="space-between" alignItems="center" py="100">
          <FolderBreadcrumbs
            folderPaths={navigation.folderPaths}
            currentFolderId={navigation.parentFolderId}
            navigateToFolder={navigation.navigateToFolder}
            checkedAssetIds={selection.checkedAssetIds}
          />
        </Box>
        <Divider />
        <Box py="500">
          {searchIsActive ? (
            <EmptyState>
              <EmptyStateTitle icon="warning">No assets matching your search</EmptyStateTitle>
            </EmptyState>
          ) : navigation.parentFolderId === null ? (
            <EmptyState>
              <EmptyStateTitle icon="error">You have not added any assets yet</EmptyStateTitle>
              <EmptyStateDescription>
                With the asset library, you can view and organize your assets from one place.
              </EmptyStateDescription>
            </EmptyState>
          ) : (
            <EmptyState>
              <EmptyStateTitle icon="error">This folder is empty</EmptyStateTitle>
            </EmptyState>
          )}
        </Box>
      </>
    );
  }

  const cannotDeleteCheckedItems = filteredAssets.some(
    (asset) => !canDeleteAsset(asset) && selection.checkedAssetIds.has(asset.id),
  );

  return (
    <DragDropProvider sensors={getSensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <Box display="flex" justifyContent="space-between" alignItems="center" py="100">
        <FolderBreadcrumbs
          folderPaths={navigation.folderPaths}
          currentFolderId={navigation.parentFolderId}
          navigateToFolder={navigation.navigateToFolder}
          checkedAssetIds={selection.checkedAssetIds}
        />
        <Box display="flex" gap="100" alignItems="center">
          <SelectionActions
            checkedAssetIds={selection.checkedAssetIds}
            canDeleteAssets={permissions.canDelete}
            cannotDeleteCheckedItems={cannotDeleteCheckedItems}
            filteredAssets={filteredAssets}
            onSelectAll={selection.onCheckAllAssets}
            onDeleteCheckedAssets={onDeleteCheckedAssetsClick}
            onClearSelection={selection.onClearCheckedAssets}
          />
          <Pagination
            page={currentPage}
            pageSize={data?.meta.pagination.size ?? ELEMENTS_PER_PAGE}
            totalItemCount={data?.meta.pagination.total ?? 0}
            onPageChange={handlePageChange}
          />
        </Box>
      </Box>
      <Divider mb="150" />
      {view.viewMode === 'list' ? (
        <AssetList
          assets={sortedAssets}
          onFolderClick={onFolderClick}
          onAssetClick={onSelect != null ? onAssetClick : undefined}
          onDelete={permissions.canDelete ? selection.onDeleteAsset : undefined}
          checkedAssetIds={selection.checkedAssetIds}
          onCheck={permissions.canEdit ? selection.onCheckAsset : undefined}
          onSelectAll={selection.onCheckAllAssets}
          onClearAll={selection.onClearCheckedAssets}
          unselectableAssetReasons={unselectableAssetReasons}
          isDraggingActive={activeAsset != null}
        />
      ) : (
        <AssetGrid
          assets={sortedAssets}
          onFolderClick={onFolderClick}
          onAssetClick={onSelect != null ? onAssetClick : undefined}
          onDelete={permissions.canDelete ? selection.onDeleteAsset : undefined}
          checkedAssetIds={selection.checkedAssetIds}
          onCheck={permissions.canEdit ? selection.onCheckAsset : undefined}
          unselectableAssetReasons={unselectableAssetReasons}
          isDraggingActive={activeAsset != null}
        />
      )}
      <DragOverlay>
        {activeAsset != null ? (
          <Box
            p="100"
            display="flex"
            alignItems="center"
            gap="050"
            className={styles.dragOverlayBox}
            style={{ width: '100px' }}
          >
            <Text size="s" weight="medium">
              Move {selectedCount} {plur('item', selectedCount)}
            </Text>
          </Box>
        ) : null}
      </DragOverlay>
    </DragDropProvider>
  );
});
